/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Alert } from 'antd'
import { SettingOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import AccountSettings from 'src/components/AccountSettings'
import {
  font,
  weightSemiBold,
  weightMedium,
  paragraphSmall,
  paragraphXSmall,
} from 'src/components/css'
import { useProfileContext } from '../context/ProfileContext'
import { getSuspensionStatusText } from '../helpers/profileFormatting'
import { ProfileSuspension, ProfileUser } from '../types'
import { CONTENT_GUTTER, MAX_CONTENT_WIDTH } from '../utils/constants'

interface Props {
  item: ProfileUser
  isSuspended: boolean
  suspensions: ProfileSuspension[] | undefined
}

const ProfileUserRow: React.FC<Props> = ({
  item,
  isSuspended,
  suspensions,
}) => {
  const { readItem } = useProfileContext()
  return (
    <div css={sectionStyle}>
      <div css={innerStyle}>
        {/* LEFT: label, name, email, suspensions */}
        <div css={leftColStyle}>
          <span css={captionLabelStyle}>
            {capitalizeWords('Your User Profile')}
          </span>

          <span css={nameStyle}>
            {item.name_first} {item.name_last}
          </span>

          <span css={emailStyle}>{item.email}</span>

          {isSuspended &&
            suspensions
              ?.filter((suspension) => suspension.is_active)
              .map((suspension) => (
                <Alert
                  key={String(suspension.id)}
                  showIcon
                  message={getSuspensionStatusText(suspension)}
                  type='error'
                  css={alertStyle}
                />
              ))}
        </div>

        {/* RIGHT: User Settings button via AccountSettings render-prop */}
        <AccountSettings
          button={({ onClick }: { onClick: () => void }) => (
            <button onClick={onClick} css={settingsButtonStyle}>
              <SettingOutlined />
              {capitalizeWords('User Settings')}
            </button>
          )}
          // This row renders from ProfileContext's reducer, not Redux, so the
          // modal's own fetchMe doesn't refresh it. Re-read /users/me2
          // (forceApi) after a save so the name/email update in place.
          onProfileUpdated={() => readItem(true)}
        />
      </div>
    </div>
  )
}

export default ProfileUserRow

const sectionStyle = css`
  background-color: ${colors.SECONDARY_400};
  padding: 16px 0;
  width: 100%;
`

const innerStyle = css`
  max-width: ${MAX_CONTENT_WIDTH}px;
  margin: 0 auto;
  padding-left: ${CONTENT_GUTTER}px;
  padding-right: ${CONTENT_GUTTER}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const leftColStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const captionLabelStyle = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 12px;
  line-height: 120%;
  letter-spacing: 1.44px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
`

const nameStyle = css`
  ${font.heading}
  ${weightSemiBold}
  display: block;
  font-size: 24px;
  line-height: 110%;
  color: ${colors.NEUTRAL_0};
  /* BarlowCondensed (SportNinja) renders caps low in its em box, leaving visual
     whitespace above the glyphs even with a tight line-height. Trim it via the
     modern text-box property where supported, with a small negative-margin
     fallback. Canlan's Neue Plak Condensed has different metrics — trimming
     there pulls the name up into the label above, so scope this to SportNinja. */
  ${!isCanlan &&
  css`
    text-box-trim: trim-start;
    text-box-edge: cap alphabetic;
    @supports not (text-box-trim: trim-start) {
      margin-top: -3px;
    }
  `}
`

const emailStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_50};
`

const alertStyle = css`
  margin-top: 8px;
`

const settingsButtonStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;
  ${paragraphXSmall}
  ${weightMedium}

  &:hover {
    opacity: 0.85;
  }
`
